import { IconButton, Stack, TextField } from '@mui/material';
import React from 'react';
import { CloseIcon, SubmitIcon } from './icons';
import { min } from 'rxjs';

interface InputCommentProps {
  value?: string;
  label?: string;
  onSubmit: (value: string) => void;
  disabled?: boolean;
  multiline?: boolean;
  minRows?: number;
  maxRows?: number;
  clearOnSubmit?: boolean;
  submitOnEnter?: boolean;
}

export function InputComment(props: InputCommentProps) {
  const {
    value: origValue,
    onSubmit,
    disabled,
    label,
    multiline,
    clearOnSubmit,
    submitOnEnter,
    minRows = 1,
    maxRows = 4,
  } = props;
  const [input, setInput] = React.useState<string>(origValue ?? '');
  const [isEditing, setIsEditing] = React.useState(false);

  const handleClose = React.useCallback(() => {
    setIsEditing(false);
    setInput(origValue ?? '');
  }, [origValue]);

  const handleSubmit = React.useCallback(
    (data: string) => {
      if (data) {
        onSubmit(data);
        setIsEditing(false);
        if (clearOnSubmit) {
          setInput('');
        }
      } else {
        handleClose();
      }
    },
    [onSubmit, clearOnSubmit, handleClose],
  );

  const handleKeyPress = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.code === 'Escape') {
      handleClose();
    }
    if (event.code === 'Enter' && submitOnEnter) {
      event.preventDefault();
      handleSubmit(input);
    }
  };

  return (
    <Stack spacing={0} width="100%" direction="row" alignItems="end" paddingTop={1}>
      <TextField
        disabled={disabled}
        label={label}
        value={input}
        onChange={(e) => setInput(e.target.value)}
        maxRows={maxRows ?? minRows}
        minRows={minRows}
        fullWidth
        onKeyUp={handleKeyPress}
        multiline={multiline}
        onFocus={() => setIsEditing(true)}
        sx={{ flexGrow: 1 }}
      />

      {isEditing && (
        <Stack direction="row" spacing={0}>
          <IconButton size="small" onClick={() => handleClose()}>
            <CloseIcon />
          </IconButton>
          <IconButton size="small" type="submit" onClick={() => handleSubmit(input)} color="success">
            <SubmitIcon />
          </IconButton>
        </Stack>
      )}
    </Stack>
  );
}
